import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getModerators, getSites, createModerator, deleteModerator, deleteModeratorCascade, getModerator } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Card,
  CardContent,
} from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Shield, Plus, MoreHorizontal, Trash2, Eye, Users, MapPin, Zap, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';

const Moderators = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [moderators, setModerators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [cascadeDeleteDialogOpen, setCascadeDeleteDialogOpen] = useState(false);
  const [selectedModerator, setSelectedModerator] = useState(null);
  const [moderatorDetails, setModeratorDetails] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [cascadeOptions, setCascadeOptions] = useState({
    deleteSites: true,
    deleteChargers: true,
    deleteClients: true
  });
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    company_name: '',
    phone: '',
    address: '',
    city: '',
    nipt: ''
  });

  const fetchData = async () => {
    try {
      const response = await getModerators();
      setModerators(response.data.items || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Gabim në ngarkimin e të dhënave');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCreateModerator = async (e) => {
    e.preventDefault();
    try {
      await createModerator(formData);
      toast.success('Moderatori u krijua');
      setDialogOpen(false);
      setFormData({
        email: '',
        password: '',
        full_name: '',
        company_name: '',
        phone: '',
        address: '',
        city: '',
        nipt: ''
      });
      fetchData();
    } catch (error) {
      console.error('Failed to create moderator:', error);
      toast.error(error.response?.data?.detail || 'Gabim në krijimin e moderatorit');
    }
  };

  const handleDeleteModerator = async () => {
    try {
      await deleteModerator(selectedModerator.id);
      toast.success('Moderatori u çaktivizua');
      setDeleteDialogOpen(false);
      setSelectedModerator(null);
      fetchData();
    } catch (error) {
      console.error('Failed to delete moderator:', error);
      toast.error(error.response?.data?.detail || 'Gabim në fshirjen e moderatorit');
    }
  };

  const openCascadeDeleteDialog = async (moderator) => {
    setSelectedModerator(moderator);
    setCascadeOptions({
      deleteSites: true,
      deleteChargers: true,
      deleteClients: true
    });
    
    // Fetch moderator details for preview
    try {
      const response = await getModerator(moderator.id);
      setModeratorDetails(response.data);
    } catch (error) {
      console.error('Failed to fetch moderator details:', error);
    }
    
    setCascadeDeleteDialogOpen(true);
  };

  const handleCascadeDeleteModerator = async () => {
    setDeleteLoading(true);
    try {
      const result = await deleteModeratorCascade(selectedModerator.id, cascadeOptions);
      const deleted = result.data.deleted;
      toast.success(
        `Moderatori u fshi! (${deleted.sites} stacione, ${deleted.chargers} karikues, ${deleted.clients} klientë)`,
        { duration: 5000 }
      );
      setCascadeDeleteDialogOpen(false);
      setSelectedModerator(null);
      setModeratorDetails(null);
      fetchData();
    } catch (error) {
      console.error('Failed to cascade delete moderator:', error);
      toast.error(error.response?.data?.detail || 'Gabim gjatë fshirjes');
    } finally {
      setDeleteLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="skeleton h-10 w-64" />
        <div className="skeleton h-96 w-full rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="moderators-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
            <Shield className="w-5 h-5 text-blue-500" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white font-outfit">Moderatorët</h1>
            <p className="text-zinc-400 text-sm">Menaxho moderatorët e stacioneve</p>
          </div>
        </div>
        <Button onClick={() => setDialogOpen(true)} data-testid="add-moderator-btn">
          <Plus className="w-4 h-4 mr-2" />
          Shto Moderator
        </Button>
      </div>

      {/* Table */}
      <Card className="bg-zinc-900/50 border-white/10">
        <CardContent className="p-0">
          {moderators.length === 0 ? (
            <div className="text-center py-12">
              <Shield className="w-16 h-16 mx-auto mb-4 text-zinc-600" />
              <p className="text-zinc-400">Nuk ka moderatorë të regjistruar</p>
              <Button className="mt-4" onClick={() => setDialogOpen(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Shto Moderator të Parë
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-white/10">
                  <TableHead className="text-zinc-400">Moderatori</TableHead>
                  <TableHead className="text-zinc-400">Stacione</TableHead>
                  <TableHead className="text-zinc-400">Karikues</TableHead>
                  <TableHead className="text-zinc-400">Klientë</TableHead>
                  <TableHead className="text-zinc-400">Statusi</TableHead>
                  <TableHead className="text-zinc-400 text-right">Veprime</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {moderators.map((moderator) => (
                  <TableRow 
                    key={moderator.id} 
                    className="border-white/5 cursor-pointer hover:bg-white/5 transition-colors"
                    onClick={() => navigate(`/moderators/${moderator.id}`)}
                    data-testid={`moderator-row-${moderator.id}`}
                  >
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center">
                          <Shield className="w-5 h-5 text-blue-500" />
                        </div>
                        <div>
                          <p className="font-medium text-white">{moderator.full_name}</p>
                          <p className="text-sm text-zinc-500">{moderator.email}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 text-zinc-300">
                        <MapPin className="w-4 h-4 text-zinc-500" />
                        {moderator.site_count || 0}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 text-zinc-300">
                        <Zap className="w-4 h-4 text-zinc-500" />
                        {moderator.charger_count || 0}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 text-zinc-300">
                        <Users className="w-4 h-4 text-zinc-500" />
                        {moderator.client_count || 0}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        moderator.is_active 
                          ? 'bg-emerald-500/10 text-emerald-400' 
                          : 'bg-red-500/10 text-red-400'
                      }`}>
                        {moderator.is_active ? 'Aktiv' : 'Joaktiv'}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-zinc-900 border-white/10">
                          <DropdownMenuItem 
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/moderators/${moderator.id}`);
                            }}
                            className="text-zinc-300 hover:!bg-white/10 hover:!text-white focus:!bg-white/10 focus:!text-white cursor-pointer"
                          >
                            <Eye className="w-4 h-4 mr-2" />
                            Shiko Profilin
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedModerator(moderator);
                              setDeleteDialogOpen(true);
                            }}
                            className="text-yellow-400 hover:!bg-yellow-500/10 hover:!text-yellow-200 focus:!bg-yellow-500/10 focus:!text-yellow-200 cursor-pointer"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Çaktivizo
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={(e) => {
                              e.stopPropagation();
                              openCascadeDeleteDialog(moderator);
                            }}
                            className="text-red-400 hover:!bg-red-500/10 hover:!text-red-200 focus:!bg-red-500/10 focus:!text-red-200 cursor-pointer"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Fshi Përgjithmonë
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-zinc-900 border-white/10 max-w-lg" data-testid="create-moderator-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">Shto Moderator të Ri</DialogTitle>
            <DialogDescription>Krijoni një moderator të ri për menaxhimin e stacioneve</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateModerator} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Emri i Plotë *</Label>
                <Input
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  placeholder="Emri Mbiemri"
                  required
                  data-testid="moderator-fullname-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Emri i Kompanisë</Label>
                <Input
                  value={formData.company_name}
                  onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                  placeholder="Emri i biznesit (opsionale)"
                  data-testid="moderator-company-input"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="email@example.com"
                  required
                  data-testid="moderator-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Fjalëkalimi *</Label>
                <Input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Minimum 6 karaktere"
                  required
                  data-testid="moderator-password-input"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Telefoni</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+355 69 XXX XXXX"
                  data-testid="moderator-phone-input"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Adresa</Label>
                <Input
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="Adresa"
                />
              </div>
              <div className="space-y-2">
                <Label>Qyteti</Label>
                <Input
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  placeholder="Qyteti"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>NIPT</Label>
              <Input
                value={formData.nipt}
                onChange={(e) => setFormData({ ...formData, nipt: e.target.value })}
                placeholder="Numri i Identifikimit Tatimor (opsionale)"
              />
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Anulo
              </Button>
              <Button type="submit" data-testid="create-moderator-submit">
                Krijo Moderatorin
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="bg-zinc-900 border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">Çaktivizo Moderatorin</DialogTitle>
            <DialogDescription>
              Jeni të sigurt që dëshironi të çaktivizoni {selectedModerator?.full_name}?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Anulo
            </Button>
            <Button variant="destructive" onClick={handleDeleteModerator}>
              Çaktivizo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CASCADE Delete Dialog */}
      <Dialog open={cascadeDeleteDialogOpen} onOpenChange={setCascadeDeleteDialogOpen}>
        <DialogContent className="bg-zinc-900 border-white/10 max-w-lg" data-testid="cascade-delete-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-400 flex items-center gap-2">
              <Trash2 className="w-5 h-5" />
              Fshi Përgjithmonë
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Kjo veprim do të fshijë përgjithmonë moderatorin dhe të gjitha të dhënat e lidhura. 
              <span className="text-red-400 font-medium"> Ky veprim nuk mund të kthehet mbrapsht!</span>
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Preview */}
            {selectedModerator && (
              <div className="p-4 rounded-lg bg-zinc-800/50 border border-white/5">
                <p className="text-white font-medium mb-2">{selectedModerator.full_name}</p>
                <p className="text-zinc-400 text-sm">{selectedModerator.email}</p>
                {moderatorDetails?.stats && (
                  <div className="grid grid-cols-3 gap-3 mt-3 pt-3 border-t border-white/5">
                    <div className="text-center">
                      <p className="text-lg font-bold text-white">{moderatorDetails.stats.sites_count || 0}</p>
                      <p className="text-xs text-zinc-500">Stacione</p>
                    </div>
                    <div className="text-center">
                      <p className="text-lg font-bold text-white">{moderatorDetails.stats.chargers_count || 0}</p>
                      <p className="text-xs text-zinc-500">Karikues</p>
                    </div>
                    <div className="text-center">
                      <p className="text-lg font-bold text-white">{moderatorDetails.stats.clients_count || 0}</p>
                      <p className="text-xs text-zinc-500">Klientë</p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Options */}
            <div className="space-y-3">
              <p className="text-sm font-medium text-zinc-300">Çfarë do të fshihet:</p>
              <div className="space-y-2">
                <label className="flex items-center gap-3 p-3 rounded-lg bg-zinc-800/30 cursor-pointer hover:bg-zinc-800/50 transition-colors">
                  <Checkbox
                    checked={cascadeOptions.deleteSites}
                    onCheckedChange={(checked) => setCascadeOptions(prev => ({ ...prev, deleteSites: checked }))}
                  />
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-emerald-500" />
                    <span className="text-white">Stacionet e caktuara</span>
                  </div>
                </label>
                <label className="flex items-center gap-3 p-3 rounded-lg bg-zinc-800/30 cursor-pointer hover:bg-zinc-800/50 transition-colors">
                  <Checkbox
                    checked={cascadeOptions.deleteChargers}
                    onCheckedChange={(checked) => setCascadeOptions(prev => ({ ...prev, deleteChargers: checked }))}
                    disabled={!cascadeOptions.deleteSites}
                  />
                  <div className="flex items-center gap-2">
                    <Zap className="w-4 h-4 text-yellow-500" />
                    <span className={cascadeOptions.deleteSites ? 'text-white' : 'text-zinc-500'}>Karikuesit në ato stacione</span>
                  </div>
                </label>
                <label className="flex items-center gap-3 p-3 rounded-lg bg-zinc-800/30 cursor-pointer hover:bg-zinc-800/50 transition-colors">
                  <Checkbox
                    checked={cascadeOptions.deleteClients}
                    onCheckedChange={(checked) => setCascadeOptions(prev => ({ ...prev, deleteClients: checked }))}
                  />
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-blue-500" />
                    <span className="text-white">Klientët e lidhur me këtë moderator</span>
                  </div>
                </label>
              </div>
            </div>

            {/* Warning */}
            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20">
              <p className="text-red-400 text-sm">
                ⚠️ Sesionet e karikimit do të arkivohen (nuk do të fshihen plotësisht) për qëllime raportimi.
              </p>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => {
                setCascadeDeleteDialogOpen(false);
                setModeratorDetails(null);
              }}
              disabled={deleteLoading}
            >
              Anulo
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleCascadeDeleteModerator}
              disabled={deleteLoading}
              data-testid="confirm-cascade-delete-btn"
            >
              {deleteLoading ? (
                <>
                  <span className="animate-spin mr-2">⏳</span>
                  Po fshihet...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Fshi Përgjithmonë
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Moderators;
